import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { useAppContext } from '@/hooks'
import {
	useCreateBranchMutation,
	useGetDeploymentPackagesQuery,
} from '@/rtkApi'
import { BranchDto } from '@/rtkApi/mddApiSliceGeneratedV2'
import { isApproverCount } from '@/utils/approverMinimum'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import { BranchRecord } from '../types'

export type BranchSourceType = 'branch' | 'release'

export type BranchSourceOption = {
	id: string
	name: string
}

type UseCreateBranchDialogArgs = {
	branches: BranchRecord[]
	initialSourceId?: string | null
	isOpen: boolean
	minimumNumberOfApprovers?: number
	onClose: () => void
	onCreated: (branchId: string) => void
	systemId?: number
}

export const useCreateBranchDialog = ({
	branches,
	initialSourceId,
	isOpen,
	minimumNumberOfApprovers = 0,
	onClose,
	onCreated,
	systemId,
}: UseCreateBranchDialogArgs) => {
	const { t } = useAppContext()
	const [createBranch, { isLoading: isCreating }] = useCreateBranchMutation()
	const [sourceType, setSourceType] = useState<BranchSourceType>('branch')
	const [sourceId, setSourceId] = useState('')
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [requiredApprovals, setRequiredApprovalsState] = useState(
		minimumNumberOfApprovers,
	)
	const approvalsTouched = useRef(false)

	const { data: releasesData } = useGetDeploymentPackagesQuery(
		{ systemId: systemId as number, pageNumber: 1, pageSize: 100 },
		{ skip: !isOpen || !systemId || sourceType !== 'release' },
	)

	const setRequiredApprovals = useCallback((value: number) => {
		approvalsTouched.current = true
		setRequiredApprovalsState(value)
	}, [])

	const reset = useCallback(() => {
		approvalsTouched.current = false
		setSourceType('branch')
		setSourceId('')
		setName('')
		setDescription('')
		setRequiredApprovalsState(minimumNumberOfApprovers)
	}, [minimumNumberOfApprovers])

	useEffect(() => {
		if (!isOpen || approvalsTouched.current) return
		setRequiredApprovalsState(minimumNumberOfApprovers)
	}, [isOpen, minimumNumberOfApprovers])

	useEffect(() => {
		if (!isOpen || !initialSourceId) return
		const listed = branches.some(
			(branch) => branch.id === initialSourceId && branch.status !== 'DELETED',
		)
		if (!listed) return
		setSourceId((current) => current || initialSourceId)
	}, [branches, initialSourceId, isOpen])

	const handleClose = useCallback(() => {
		reset()
		onClose()
	}, [onClose, reset])

	const sourceOptions: BranchSourceOption[] = useMemo(() => {
		if (sourceType === 'release') {
			return (releasesData?.content ?? []).flatMap((release) =>
				release.id != null
					? [{ id: String(release.id), name: release.name ?? String(release.id) }]
					: [],
			)
		}
		return branches
			.filter((branch) => branch.status !== 'DELETED')
			.map((branch) => ({ id: branch.id, name: branch.name }))
	}, [branches, releasesData?.content, sourceType])

	const approvalsValid = isApproverCount(
		requiredApprovals,
		minimumNumberOfApprovers,
	)

	const handleCreate = useCallback(async () => {
		const trimmedName = name.trim()
		if (!trimmedName || !systemId || !approvalsValid) return

		const branchDto: BranchDto = {
			description: description.trim() || undefined,
			name: trimmedName,
			requiredApprovals,
			status: 'ACTIVE',
		}
		if (sourceType === 'branch' && sourceId) {
			branchDto.sourceBranch = { id: Number(sourceId) }
		}
		if (sourceType === 'release' && sourceId) {
			branchDto.sourceReleaseId = Number(sourceId)
		}

		try {
			const created = await createBranch({ branchDto, systemId }).unwrap()
			toast(t('BRANCHES_CREATE_SUCCESS'), { type: 'success' })
			handleClose()
			if (created.id != null) onCreated(String(created.id))
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'CreateBranchDialog.handleCreate' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'BRANCHES_CREATE_FAILED',
				t,
			})
		}
	}, [
		approvalsValid,
		createBranch,
		description,
		handleClose,
		name,
		onCreated,
		requiredApprovals,
		sourceId,
		sourceType,
		systemId,
		t,
	])

	return {
		approvalsValid,
		description,
		handleClose,
		handleCreate,
		isCreating,
		minimumNumberOfApprovers,
		name,
		requiredApprovals,
		setDescription,
		setName,
		setRequiredApprovals,
		setSourceId,
		setSourceType,
		sourceId,
		sourceOptions,
		sourceType,
	}
}
